import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Layers3, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';

import axiosInstance from 'src/common/axiosInstance';
import { AuthContext } from 'src/context/AuthContext';
import { WsDropdown } from 'src/stage/WsControls';
import { clearLeads, getDevelopers, getPipelines } from 'src/features/MiniCRM/CachedData';
import { getLeadFields, getLeadSchema } from 'src/features/MiniCRM/services/leadFieldService';
import LeadModal from 'src/features/MiniCRM/components/Leads/LeadModal';
import { StageDialog } from './StageKit';
import { apiMessage } from './crmWork';

// AUTO-CRM-32: add a lead in a few seconds. Name and a way to reach them is
// enough; "More details" opens the full form with the pipeline's own fields.
const SOURCES = ['Website', 'WhatsApp', 'Phone call', 'Walk-in', 'Referral', 'Instagram', 'IndiaMART'];
const BASIC = new Set(['name', 'phone', 'email', 'source']);

export default function QuickAddLead({ onClose, onCreated, defaultPipelineId }) {
  const { currentUser } = useContext(AuthContext);
  const workspaceId = localStorage.getItem('autoappy_current_workspace_id') || currentUser?.local_workspace_id || '';
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(defaultPipelineId || localStorage.getItem('selectedPipelineId') || '');
  const [firstStage, setFirstStage] = useState(null);
  const [schema, setSchema] = useState(null);
  const [requiredCustom, setRequiredCustom] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', source: '', owner: 'me' });
  const [extra, setExtra] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fullForm, setFullForm] = useState(false);

  useEffect(() => {
    let active = true;
    getPipelines(workspaceId).then((rows) => {
      const list = Array.isArray(rows) ? rows : rows?.data || [];
      if (!active) return;
      setPipelines(list);
      if (!list.some((pipeline) => String(pipeline.id) === String(pipelineId))) setPipelineId(String((list.find((pipeline) => pipeline.is_default) || list[0])?.id || ''));
    }).catch(() => {});
    getDevelopers(workspaceId).then((rows) => { if (active) setPeople((Array.isArray(rows) ? rows : rows?.data || []).map((row) => row.user || row).filter((user) => user?.id)); }).catch(() => {});
    return () => { active = false; };
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pipelineId || !workspaceId) return undefined;
    let active = true;
    setSchema(null); setFirstStage(null); setRequiredCustom([]);
    Promise.allSettled([
      getLeadSchema(workspaceId, pipelineId),
      getLeadFields(workspaceId, { pipelineId }),
      axiosInstance.get('/analytics/crm-overview', { params: { pipeline_id: pipelineId, days: 7, mode: 'queue' } }),
    ]).then(([schemaResult, fieldsResult, overview]) => {
      if (!active) return;
      setSchema(schemaResult.status === 'fulfilled' ? schemaResult.value : { starter_fields: [] });
      if (fieldsResult.status === 'fulfilled') setRequiredCustom((fieldsResult.value || []).filter((field) => field.is_active && field.is_required));
      const funnel = overview.status === 'fulfilled' ? overview.value.data?.data?.funnel || [] : [];
      setFirstStage(funnel.find((stage) => !stage.is_closed) || funnel[0] || null);
    });
    return () => { active = false; };
  }, [pipelineId, workspaceId]);

  const requiredStarters = useMemo(() => (schema?.starter_fields || []).filter((field) => field.is_required && !BASIC.has(field.key)), [schema]);
  const phoneRequired = (schema?.starter_fields || []).some((field) => field.key === 'phone' && field.is_required);
  const emailRequired = (schema?.starter_fields || []).some((field) => field.key === 'email' && field.is_required);
  const set = (key) => (event) => setForm((value) => ({ ...value, [key]: event.target.value }));
  const ownerOptions = [{ value: 'me', label: 'Me' }, { value: 'none', label: 'No owner yet' }, ...people.filter((person) => person.id !== currentUser?.id).map((person) => ({ value: String(person.id), label: person.name || person.email }))];
  const canSave = form.name.trim() && (!phoneRequired || form.phone.trim()) && (!emailRequired || form.email.trim()) && requiredStarters.every((field) => String(extra[field.key] || '').trim()) && !requiredCustom.length;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        source: form.source.trim() || null,
        assigned_user_id: form.owner === 'me' ? currentUser?.id : form.owner === 'none' ? null : form.owner,
        ...Object.fromEntries(requiredStarters.map((field) => [field.key, extra[field.key]])),
      };
      if (firstStage?.id) payload.lead_stage_id = firstStage.id;
      const response = await axiosInstance.post('/leads', payload, { headers: { 'X-Workspace-ID': workspaceId } });
      const lead = response.data?.data || response.data;
      clearLeads();
      window.dispatchEvent(new Event('leads-reset'));
      toast.success(`${payload.name} added${firstStage?.name ? ` to ${firstStage.name}` : ''}`);
      onCreated?.(lead);
      onClose();
    } catch (saveError) {
      setError(apiMessage(saveError, 'We couldn’t add this lead. Check the details and try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (fullForm) {
    return <LeadModal workspaceId={workspaceId} pipelineId={pipelineId} onClose={onClose} onSuccess={() => { clearLeads(); window.dispatchEvent(new Event('leads-reset')); toast.success('Lead added'); onCreated?.(); onClose(); }} />;
  }

  return (
    <StageDialog
      title="Add a lead"
      subtitle="Just the basics. You can add everything else later."
      icon={UserPlus}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          {error ? <p className="crm-kit-error" role="alert">{error}</p> : <button type="button" className="ws-btn ws-btn--plain" onClick={() => setFullForm(true)} disabled={busy}>More details</button>}
          <span className="ws-grow" />
          <button type="button" className="ws-btn ws-btn--quiet" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--primary" onClick={save} disabled={busy || !canSave}><UserPlus size={15} />{busy ? 'Adding…' : 'Add lead'}</button>
        </>
      )}
    >
      <label className="crm-kit-section">
        <span className="crm-kit-label">Name *</span>
        <input className="ws-input crm-kit-input" value={form.name} onChange={set('name')} placeholder="Person or business name" maxLength={255} autoComplete="off" />
      </label>
      <div className="crm-kit-grid">
        <label className="crm-kit-section">
          <span className="crm-kit-label">Phone{phoneRequired ? ' *' : ''}</span>
          <input className="ws-input crm-kit-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" maxLength={40} autoComplete="off" />
        </label>
        <label className="crm-kit-section">
          <span className="crm-kit-label">Email{emailRequired ? ' *' : ''}</span>
          <input className="ws-input crm-kit-input" type="email" value={form.email} onChange={set('email')} placeholder="name@company.com" maxLength={255} autoComplete="off" />
        </label>
      </div>
      <div className="crm-kit-section">
        <span className="crm-kit-label">Where did they come from?</span>
        <div className="crm-chip-row" role="radiogroup" aria-label="Source">
          {SOURCES.map((source) => (
            <button key={source} type="button" role="radio" aria-checked={form.source === source} className={`crm-choice crm-choice--soft${form.source === source ? ' is-on' : ''}`} onClick={() => setForm((value) => ({ ...value, source: value.source === source ? '' : source }))}>{source}</button>
          ))}
        </div>
      </div>
      {requiredStarters.map((field) => (
        <label key={field.key} className="crm-kit-section">
          <span className="crm-kit-label">{field.label} *</span>
          <input className="ws-input crm-kit-input" value={extra[field.key] || ''} onChange={(event) => setExtra((value) => ({ ...value, [field.key]: event.target.value }))} maxLength={255} />
        </label>
      ))}
      <div className="crm-kit-grid">
        <div className="crm-kit-section">
          <span className="crm-kit-label">Pipeline</span>
          {pipelines.length ? <WsDropdown label="Pipeline" icon={Layers3} value={String(pipelineId)} onChange={setPipelineId} options={pipelines.map((pipeline) => ({ value: String(pipeline.id), label: pipeline.name }))} /> : <span className="ws-muted">Default pipeline</span>}
          <span className="ws-muted crm-kit-hint">{firstStage ? `Starts in “${firstStage.name}”` : 'Starts in the first stage'}</span>
        </div>
        <div className="crm-kit-section">
          <span className="crm-kit-label">Owner</span>
          <WsDropdown label="Owner" value={form.owner} onChange={(owner) => setForm((value) => ({ ...value, owner }))} options={ownerOptions} align="right" />
          <span className="ws-muted crm-kit-hint">The teammate who follows up</span>
        </div>
      </div>
      {requiredCustom.length > 0 && (
        <p className="ws-notice" role="status">This pipeline asks for {requiredCustom.map((field) => field.label).join(', ')}. Use <button type="button" className="ws-btn ws-btn--plain" onClick={() => setFullForm(true)}>More details</button> to fill them in.</p>
      )}
    </StageDialog>
  );
}
